import PropTypes from "prop-types";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { customFieldsRequest } from "./api";
import CustomFieldControl from "./CustomFieldControl";
import useCustomFieldsCapability from "./useCustomFieldsCapability";
import useCustomFieldsResource from "./useCustomFieldsResource";

const createLead = ({ teamId, contact, customFields }) =>
  customFieldsRequest({
    teamId,
    path: "/leads",
    method: "POST",
    body: { lead: contact, custom_fields: customFields },
  });

const AddLeadAdditionalDetailsSection = forwardRef(
  (
    {
      teamId,
      onCreated,
      onFailure,
      onStateChange,
      createContact = createLead,
      contactsHref = "/recruiter/leads",
    },
    ref,
  ) => {
    const capability = useCustomFieldsCapability({ teamId });
    const displayed = capability.variant === "displayed";
    const resource = useCustomFieldsResource({
      teamId,
      enabled: displayed,
      path: "/custom_fields/new_contact",
    });
    const [draft, setDraft] = useState(null);
    const [snapshot, setSnapshot] = useState(null);
    const [errors, setErrors] = useState({});
    const [failure, setFailure] = useState(null);
    const [review, setReview] = useState(false);
    const [busy, setBusy] = useState(false);
    const inFlight = useRef(false);
    const container = useRef(null);
    const catalogChanged =
      !!snapshot &&
      !!resource.data &&
      JSON.stringify(resource.data.definitions) !==
        JSON.stringify(snapshot.definitions);
    const recoveryRequired = failure?.uncertain || failure?.status === 409;
    const blocked =
      capability.loading ||
      !!capability.error ||
      !capability.variant ||
      recoveryRequired ||
      catalogChanged ||
      (displayed && (resource.loading || !!resource.error || !snapshot));

    useEffect(() => {
      if (resource.data && !snapshot) {
        setSnapshot(resource.data);
        setDraft(
          Object.fromEntries(
            resource.data.definitions.map((field) => [
              field.id,
              field.default_value ?? null,
            ]),
          ),
        );
      }
    }, [resource.data, snapshot]);
    useEffect(() => {
      onStateChange?.({
        busy,
        blocked,
        dirty:
          !!draft &&
          !!snapshot &&
          snapshot.definitions.some(
            (field) =>
              JSON.stringify(draft[field.id]) !==
              JSON.stringify(field.default_value ?? null),
          ),
      });
    }, [busy, blocked, draft, snapshot, onStateChange]);
    useEffect(() => {
      container.current?.querySelector('[aria-invalid="true"]')?.focus();
    }, [errors]);

    useImperativeHandle(
      ref,
      () => ({
        submit(lead) {
          if (!displayed && !blocked) {
            return false;
          }
          if (inFlight.current || blocked || !snapshot || !draft) {
            onFailure({ errors: {} });
            return true;
          }
          const invalid = {};
          snapshot.definitions.forEach((field) => {
            const value = draft[field.id];
            if (
              field.required &&
              (value == null ||
                (typeof value === "string" && !value.trim()) ||
                (Array.isArray(value) && !value.length))
            ) {
              invalid[field.id] = `${field.label} is required.`;
            }
          });
          setErrors(invalid);
          if (Object.keys(invalid).length) {
            onFailure({ errors: {} });
            return true;
          }
          inFlight.current = true;
          setBusy(true);
          setFailure(null);
          createContact({
            teamId,
            contact: lead,
            customFields: {
              catalog_revision: snapshot.catalog_revision,
              changes: snapshot.definitions.map((field) => ({
                definition_id: field.id,
                value: draft[field.id] ?? null,
              })),
            },
          })
            .then(
              (created) => onCreated(created),
              (error) => {
                setFailure(error);
                if (error.errors && typeof error.errors === "object") {
                  setErrors(
                    Object.fromEntries(
                      snapshot.definitions
                        .filter(
                          (field) => typeof error.errors[field.id] === "string",
                        )
                        .map((field) => [field.id, error.errors[field.id]]),
                    ),
                  );
                }
                onFailure(
                  error.details?.duplicate_emails ||
                    error.details?.duplicate_agent_ids
                    ? error.details
                    : error.errors || error.details || {},
                );
              },
            )
            .finally(() => {
              inFlight.current = false;
              setBusy(false);
            });
          return true;
        },
      }),
      [
        displayed,
        capability.loading,
        blocked,
        snapshot,
        draft,
        onFailure,
        onCreated,
        createContact,
        teamId,
      ],
    );

    if (!capability.loading && (capability.error || !capability.variant)) {
      return (
        <div role="alert">
          Additional Details could not be checked. Try again before creating
          this contact.{" "}
          <button
            type="button"
            className="btn btn-link"
            onClick={capability.reload}
          >
            Try Again
          </button>
        </div>
      );
    }
    if (!displayed) {
      return null;
    }
    if (resource.loading) {
      return <p role="status">Loading Additional Details.</p>;
    }
    if (resource.error || !resource.data) {
      return (
        <div role="alert">
          Additional Details could not be loaded.{" "}
          <button
            type="button"
            className="btn btn-link"
            onClick={resource.reload}
          >
            Try Again
          </button>
        </div>
      );
    }
    const recover = recoveryRequired || catalogChanged;
    const reviewDraft = () => {
      if (inFlight.current) {
        return;
      }
      setSnapshot(resource.data);
      setDraft((current) =>
        Object.fromEntries(
          resource.data.definitions.map((field) => [
            field.id,
            Object.hasOwn(current || {}, field.id) ? current[field.id] : null,
          ]),
        ),
      );
      setFailure(null);
      setErrors({});
      setReview(false);
    };
    return (
      <section ref={container} className="border-top pt-3 mt-3">
        {!!resource.data.definitions.length && <h5>Additional Details</h5>}
        {Object.keys(errors).length > 0 && (
          <p role="alert">
            {failure
              ? "Review the highlighted fields in Additional Details before saving."
              : "Complete the required field in Additional Details before saving."}
          </p>
        )}
        {failure && (
          <div role="alert" className="alert alert-warning">
            {failure.uncertain
              ? "We couldn't confirm whether this contact was created. Check your contacts before trying again."
              : "The contact could not be saved. Review your entries before trying again."}
            {recover && (
              <button
                type="button"
                className="btn btn-link"
                onClick={() => {
                  setReview(true);
                  resource.reload();
                }}
              >
                Reload and Review
              </button>
            )}
          </div>
        )}
        {(review || (catalogChanged && !recoveryRequired)) && (
          <div className="alert alert-warning">
            <p>
              Review your retained draft against the current field definitions.
              New defaults have not been applied.
            </p>
            {failure?.uncertain && (
              <a href={contactsHref} target="_blank" rel="noopener noreferrer">
                Check existing contacts
              </a>
            )}
            <button
              type="button"
              className="btn btn-primary ml-2"
              disabled={busy}
              onClick={reviewDraft}
            >
              Use Reviewed Draft
            </button>
          </div>
        )}
        {resource.data.definitions.map((field) => (
          <CustomFieldControl
            key={field.id}
            field={field}
            value={draft?.[field.id] ?? null}
            error={errors[field.id]}
            disabled={busy || recover}
            onChange={(value) =>
              setDraft((current) => ({ ...current, [field.id]: value }))
            }
          />
        ))}
      </section>
    );
  },
);
AddLeadAdditionalDetailsSection.displayName = "AddLeadAdditionalDetailsSection";
AddLeadAdditionalDetailsSection.propTypes = {
  teamId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onCreated: PropTypes.func.isRequired,
  onFailure: PropTypes.func.isRequired,
  onStateChange: PropTypes.func,
  createContact: PropTypes.func,
  contactsHref: PropTypes.string,
};
export default AddLeadAdditionalDetailsSection;
